import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { FS, LS, THEME_MODE } from "src/config/constants";
import { useWeb3Connect } from "src/wallet-connection/context/we3-connect-context";
import { useProviderContext } from "src/ether-utils/context/providerContext";
import { getChainContractAddresses } from "src/config/addresses";
import { ethers } from "ethers";
import FactoryRegistry from "src/assets/abis/poolMonitoring/FactoryRegistry.json";

const ConfigContext = React.createContext({
  themeMode: "light",
  toggleThemeMode: () => {},
  addresses: {},
  getAddresses: () => {},
  fetchingAddress: FS.INITIAL,
});

const getDefaultThemeMode = () => {
  let theme = window.localStorage.getItem(LS.THEME);
  if (!["light", "dark"].includes(theme)) {
    theme = "light";
    window.localStorage.setItem(LS.THEME, theme);
  }
  return theme;
};

const ConfigProvider = ({ children }) => {
  const { chain } = useWeb3Connect();
  const { currentProvider } = useProviderContext();

  const [theme, setTheme] = useState(getDefaultThemeMode);
  const [addresses, setAddresses] = useState({});
  const [fetchingAddress, setFetchingAddress] = useState(FS.INITIAL);

  const toggleTheme = useCallback(() => {
    if (theme === THEME_MODE.LIGHT) {
      setTheme(THEME_MODE.DARK);
      window.localStorage.setItem(LS.THEME, THEME_MODE.DARK);
    } else {
      setTheme(THEME_MODE.LIGHT);
      window.localStorage.setItem(LS.THEME, THEME_MODE.LIGHT);
    }
  }, [theme]);

  const getAddresses = useCallback(async () => {
    if (currentProvider) {
      setFetchingAddress(FS.FETCHING);
      const _addresses = getChainContractAddresses(chain.chainId);
      const factoryRegistryContract = new ethers.Contract(
        _addresses.FACTOR_REGISTRY_ADDRESS,
        FactoryRegistry,
        currentProvider
      );
      const addressesProviderFactoryAddress = await factoryRegistryContract.getAddressesProviderFactory();
      const interestRateStrategyFactoryAddress = await factoryRegistryContract.getInterestRateFactory();
      setAddresses({ ..._addresses, addressesProviderFactoryAddress, interestRateStrategyFactoryAddress });
      setFetchingAddress(FS.SUCCESS);
    }
  }, [currentProvider, chain.chainId]);

  const contextData = useMemo(
    () => ({
      themeMode: theme,
      toggleThemeMode: toggleTheme,
      addresses,
      getAddresses,
      fetchingAddress,
    }),
    [theme, addresses, getAddresses, fetchingAddress]
  );

  return <ConfigContext.Provider value={contextData}>{children}</ConfigContext.Provider>;
};

ConfigProvider.propTypes = {
  children: PropTypes.node,
};

export default ConfigProvider;

export const useConfigContext = () => React.useContext(ConfigContext);

export const useConfigSelector = (selectorFn) => {
  const result = useConfigContext();
  return useMemo(() => selectorFn(result), [result]);
};
